/**
 * NOYKA Locale Engine — Detection
 *
 * Resolves the best locale using the priority chain:
 * 1) ?lang= query param
 * 2) cookie noyka_lang
 * 3) localStorage noyka_lang
 * 4) navigator.language / navigator.languages
 * 5) region fallback
 * 6) 'en'
 */

import {
  LOCALE_QUERY_PARAM,
  LOCALE_STORAGE_KEY,
  LOCALE_COOKIE_KEY,
  DEFAULT_LOCALE,
  SUPPORTED_LOCALES,
  REGION_LOCALE_MAP,
  type SupportedLocale,
} from './constants';

/** Normalize raw codes like 'en-US' -> 'en', 'tr-TR' -> 'tr' */
export function normalizeLocale(raw: string): string {
  return raw.split('-')[0].split('_')[0].toLowerCase();
}

/** Check if a code is in the supported list */
export function isSupportedLocale(code: string): code is SupportedLocale {
  return (SUPPORTED_LOCALES as readonly string[]).includes(code);
}

/** Read ?lang= from current URL */
export function getQueryLocale(): string | null {
  if (typeof window === 'undefined') return null;
  const params = new URLSearchParams(window.location.search);
  const raw = params.get(LOCALE_QUERY_PARAM);
  if (!raw) return null;
  const norm = normalizeLocale(raw);
  return isSupportedLocale(norm) ? norm : null;
}

/** Read from cookie */
export function getCookieLocale(): string | null {
  if (typeof document === 'undefined') return null;
  const match = document.cookie
    .split('; ')
    .find((row) => row.startsWith(`${LOCALE_COOKIE_KEY}=`));
  if (!match) return null;
  const norm = normalizeLocale(match.split('=')[1]);
  return isSupportedLocale(norm) ? norm : null;
}

/** Read from localStorage */
export function getStorageLocale(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(LOCALE_STORAGE_KEY)
      || window.localStorage.getItem('i18nextLng');
    if (!raw) return null;
    const norm = normalizeLocale(raw);
    return isSupportedLocale(norm) ? norm : null;
  } catch {
    return null;
  }
}

/** Derive from navigator */
export function getBrowserLocale(): string | null {
  if (typeof navigator === 'undefined') return null;
  const langs = navigator.languages?.length ? navigator.languages : [navigator.language];
  for (const raw of langs) {
    const norm = normalizeLocale(raw);
    if (isSupportedLocale(norm)) return norm;
  }
  return null;
}

/** Derive from region */
export function getRegionLocale(): string | null {
  if (typeof navigator === 'undefined') return null;
  const langs = navigator.languages?.length ? navigator.languages : [navigator.language];
  for (const raw of langs) {
    const parts = raw.toLowerCase().split('-');
    const country = parts[1] || parts[0];
    const mapped = REGION_LOCALE_MAP[country];
    if (mapped) return mapped;
    const langMapped = REGION_LOCALE_MAP[parts[0]];
    if (langMapped) return langMapped;
  }
  return null;
}

/**
 * Resolve the best locale using the full priority chain.
 */
export function resolveLocale(): SupportedLocale {
  return (
    getQueryLocale()
    || getCookieLocale()
    || getStorageLocale()
    || getBrowserLocale()
    || getRegionLocale()
    || DEFAULT_LOCALE
  ) as SupportedLocale;
}
